import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useAnnouncements, useCloseAnnouncement, useActions } from '@mod/api'
import {
  AnnouncementCardError,
  APIErrorCard,
  Grid,
  GridItem,
  DashboardMasthead,
  AnnouncementCarousel,
  AnnouncementsThrobber,
  MainContent,
  SectionTitle,
  ActionCard,
  DefaultCardThrobber,
  CTA,
  SrOnly,
  RichText,
  Typography,
  LatestFeatures,
} from '@mod/components'
import { useMutation } from '@mod/components/utils'
import MastheadBanner from '@mod/components/MastheadBanner'
import { FixedObject, FluidObject } from 'gatsby-image'
import { LatestFeaturesSlide } from '@mod/components/LatestFeatures/LatestFeatures.types'
import { LocalHubLoader } from '@mod/components/LocalHub'
import { useVideoContext } from '../Video/Video.context'

interface Props {
  background: FluidObject
  dismissedMessage: {
    title: string
    intro?: string
  }
  site: SiteProfile
  latestMessage?: string
  badge?: FixedObject
  cta?: {
    ctaText?: string
    ctaLinkText?: string
    ctaLink?: string
  }
  alwaysShowCta?: boolean
  showCtaAsBanner?: boolean
  latestFeatures?: LatestFeaturesSlide[]
}

const Prompt = styled.div.attrs(() => ({
  'aria-live': 'assertive',
  role: 'alert',
}))`
  margin-top: ${props => props.theme.spacing(4)};
  &:empty {
    display: none;
  }
`

const StyledCTA = styled(CTA)`
  ${props => `@media screen and (min-width: ${props.theme.breakpoints.BREAKPOINT_MD})`} {
    max-width: ${props => props.theme.sizing.CTA_DESKTOP_MAX_WIDTH}px;
  }
`

export const Latest: React.FunctionComponent<Props> = ({
  background,
  dismissedMessage,
  site,
  latestMessage = 'Hello, here’s the latest',
  badge,
  cta,
  alwaysShowCta,
  showCtaAsBanner,
  latestFeatures,
}) => {
  const { title: dismissedTitle, intro: dismissedIntro } = dismissedMessage

  const { ctaLink, ctaLinkText, ctaText } = cta

  const [announcementsStatus, announcements] = useAnnouncements()
  const [actionsStatus, actions] = useActions()
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(-1)
  const [announcementsCleared, setAnnouncementsCleared] = useState<boolean>(false)
  const closeAnnouncement = useCloseAnnouncement(selectedAnnouncement)

  const subscriber = mutations => {
    mutations.forEach(mutation => {
      if (mutation.target.nodeName !== 'OL') {
        return
      }
      if (mutation.target.childNodes.length === 0) {
        setAnnouncementsCleared(true)
      }
    })
  }

  const announcementsCarousel = useMutation(subscriber)

  useEffect(() => {
    if (selectedAnnouncement !== -1) {
      closeAnnouncement()
    }
  }, [selectedAnnouncement])

  useEffect(() => {
    if (announcementsStatus === 'ok') {
      if (announcements.length === 0) {
        setAnnouncementsCleared(true)
      }
    }
  }, [announcementsStatus])

  const Announcements = React.useMemo(() => {
    if (announcementsStatus === 'pending') {
      return <AnnouncementsThrobber />
    }

    if (announcementsStatus === 'error') {
      return <AnnouncementCardError />
    }

    if (announcementsStatus !== 'ok') {
      return null
    }

    return (
      <AnnouncementCarousel
        announcements={announcements.map((a, i) => {
          return {
            id: `announcement-${i}`,
            announcement: {
              category: a.category?.categoryName,
              title: a.title,
              summary: a.summary,
              link: `/announcement/${a.id}`,
              announcementsLength: announcements.length,
              expiryDate: a.expiryDate,
              index: i + 1,
              onClose: () => {
                setSelectedAnnouncement(a.id)
              },
            },
          }
        })}
        ref={announcementsCarousel}
      />
    )
  }, [announcements, announcementsCarousel, announcementsStatus])

  const renderActions = () => {
    if (actionsStatus === 'pending') {
      return (
        <Grid>
          <GridItem xs={12}>
            <SectionTitle>&nbsp;</SectionTitle>
          </GridItem>

          {['a', 'b', 'c', 'd'].map(x => (
            <GridItem xs={12} md={4} key={x}>
              <DefaultCardThrobber />
            </GridItem>
          ))}
        </Grid>
      )
    }
    if (actionsStatus === 'error') {
      return (
        <Grid>
          <GridItem xs={12}>
            <SectionTitle>Actions</SectionTitle>
          </GridItem>
          <GridItem xs={12} md={4}>
            <APIErrorCard displayName="actions" tag="Actions" />
          </GridItem>
        </Grid>
      )
    }

    if (actionsStatus !== 'ok' || !actions.length) {
      return null
    }

    const urgentCount = actions.filter(a => a.status === 'urgent').length

    return (
      <Grid>
        <GridItem xs={12}>
          <SectionTitle>
            You have {urgentCount} urgent action{urgentCount === 1 ? '' : 's'}
          </SectionTitle>
        </GridItem>

        {actions.map(action => (
          <GridItem xs={12} md={4} key={action.title}>
            <ActionCard
              summary={action.summary}
              title={action.title}
              date={action.dueDate}
              status={action.status}
              link={action.link}
              linkText={action.linkText}
            />
          </GridItem>
        ))}
      </Grid>
    )
  }

  const act = renderActions()

  const allClear = !act && announcementsCleared

  const title = allClear ? dismissedTitle : latestMessage
  const summary = allClear ? dismissedIntro : ''

  const renderCTA = () => {
    return (
      ctaText &&
      !showCtaAsBanner && (
        <>
          <Typography as="h2" color="TEXT_WHITE" spacingUnits={[0, 0, 2, 0]} variant="HEADING_S">
            {ctaText}
          </Typography>
          <StyledCTA href={ctaLink.replace(/\/home-my-(navy|raf)/gi, '')}>{ctaLinkText}</StyledCTA>
        </>
      )
    )
  }

  const showCtaBanner = ctaText && showCtaAsBanner && (alwaysShowCta || allClear)

  // Latest Features

  const { setVideoID } = useVideoContext()

  return (
    <>
      {showCtaBanner && (
        <MastheadBanner
          title={ctaLinkText}
          alert={{ link: cta.ctaLink, message: cta.ctaText }}
          id="cta-banner"
          variant="secondary"
        />
      )}
      <DashboardMasthead
        variant="latest"
        title={title}
        summary={summary}
        background={background}
        badge={badge}
        afixToSummary={
          allClear ? (
            <Prompt>
              <SrOnly>{dismissedTitle}</SrOnly>
              <SrOnly as="div">
                {!!dismissedIntro && <RichText innerHTML={dismissedIntro} />}
              </SrOnly>
              {renderCTA()}
            </Prompt>
          ) : (
            <>{alwaysShowCta ? <Prompt>{renderCTA()}</Prompt> : ''}</>
          )
        }
        // AnnouncementCarousel={!announcementsCleared && Announcements}
        AnnouncementCarousel={Announcements}
      />
      <MainContent>
        <LocalHubLoader />
      </MainContent>

      {!!act && <MainContent>{act}</MainContent>}

      {latestFeatures && latestFeatures.length > 0 && (
        <MainContent>
          <SectionTitle variant='lightblue'>New App Features</SectionTitle>
          <LatestFeatures slides={latestFeatures} setVideo={setVideoID} />
        </MainContent>
      )}
    </>
  )
}
